import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Volume2, VolumeX, Play, Bell, BellOff } from "lucide-react";
import {
  getSoundEnabled, getSoundVolume, setSoundEnabled, setSoundVolume,
} from "@/lib/soundSettings";
import { notifySound } from "@/lib/notifySound";
import {
  AVAILABLE_SOUNDS,
  EVENT_CATEGORIES,
  getEventSoundMap,
  setEventSound,
  subscribeEventMapChanges,
} from "@/lib/soundEventMap";
import {
  getWebNotificationPermission,
  requestWebNotificationPermission,
  isWebNotificationSupported,
  type WebNotifPermission,
} from "@/lib/webNotifications";

// Throttle while dragging so it doesn't machine-gun the sound.
const DRAG_THROTTLE_MS = 220;

export function SoundNotificationSettingsCard() {
  const [enabled, setEnabledState] = useState<boolean>(true);
  const [volume, setVolumeState] = useState<number>(0.6);
  const [webPerm, setWebPerm] = useState<WebNotifPermission>("default");
  const [eventMap, setEventMap] = useState<Record<string, string>>(() => getEventSoundMap());
  const webSupported = isWebNotificationSupported();

  useEffect(() => {
    setEnabledState(getSoundEnabled());
    setVolumeState(getSoundVolume());
    setWebPerm(getWebNotificationPermission());
    const unsub = subscribeEventMapChanges(() => setEventMap(getEventSoundMap()));
    return unsub;
  }, []);

  const handleEnableBrowser = async () => {
    const r = await requestWebNotificationPermission();
    setWebPerm(r);
  };

  const handleToggle = (v: boolean) => {
    setEnabledState(v);
    setSoundEnabled(v);
  };

  // Drag ding: throttled preview while dragging; one final ding on release.
  const [lastDragPlay, setLastDragPlay] = useState<number>(0);
  const previewAtVolume = (key: string) => {
    const wasEnabled = getSoundEnabled();
    if (!wasEnabled) setSoundEnabled(true);
    notifySound.play(key);
    if (!wasEnabled) setTimeout(() => setSoundEnabled(false), 50);
  };

  const handleVolumeChange = (vals: number[]) => {
    const v = (vals[0] ?? 60) / 100;
    setVolumeState(v);
    setSoundVolume(v);
    const now = Date.now();
    if (now - lastDragPlay >= DRAG_THROTTLE_MS) {
      setLastDragPlay(now);
      previewAtVolume("alert-ping");
    }
  };
  const handleVolumeCommit = () => {
    // Guaranteed final ding at release so the user hears the exact level.
    previewAtVolume("alert-ping");
  };

  const preview = (key: string) => {
    previewAtVolume(key);
  };

  const handleReassign = (categoryId: string, soundKey: string) => {
    setEventSound(categoryId, soundKey);
    setEventMap(getEventSoundMap());
    preview(soundKey);
  };

  return (
    <Card className="p-6 mb-6">
      <div className="flex items-center gap-2 mb-6">
        {enabled ? <Volume2 className="w-5 h-5 text-primary" /> : <VolumeX className="w-5 h-5 text-muted-foreground" />}
        <h2 className="text-xl font-semibold">In-App Sound Notifications</h2>
      </div>

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Label htmlFor="sound-enabled" className="font-medium">Play sound on new notifications</Label>
            <p className="text-sm text-muted-foreground">
              Chimes for quote requests, bids, offers, and live alerts.
            </p>
          </div>
          <Switch id="sound-enabled" checked={enabled} onCheckedChange={handleToggle} />
        </div>

        <div className={enabled ? "" : "opacity-50 pointer-events-none"}>
          <div className="flex items-center justify-between mb-2">
            <Label className="font-medium">Volume</Label>
            <span className="text-sm tabular-nums text-muted-foreground">{Math.round(volume * 100)}%</span>
          </div>
          <Slider
            value={[Math.round(volume * 100)]}
            min={0}
            max={100}
            step={5}
            onValueChange={handleVolumeChange}
            onValueCommit={handleVolumeCommit}
          />
          <p className="text-xs text-muted-foreground mt-2">
            You'll hear a short ding as you drag so you can dial in the volume.
          </p>
        </div>

        <div className="border-t border-border pt-4">
          <h3 className="text-sm font-medium text-muted-foreground mb-3">Sound for each event</h3>
          <div className="space-y-3">
            {EVENT_CATEGORIES.map((cat) => {
              const current = eventMap[cat.id] ?? cat.defaultSound;
              return (
                <div
                  key={cat.id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-md border border-border p-3"
                >
                  <div className="min-w-0">
                    <Label className="font-medium">{cat.label}</Label>
                    <p className="text-xs text-muted-foreground">{cat.desc}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Select
                      value={current}
                      onValueChange={(v) => handleReassign(cat.id, v)}
                    >
                      <SelectTrigger className="w-[180px] h-8 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {AVAILABLE_SOUNDS.map((s) => (
                          <SelectItem key={s.key} value={s.key}>
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => preview(current)}
                      className="gap-1.5"
                      title="Preview"
                    >
                      <Play className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Browsers may block the first sound until you click somewhere on the page.
          </p>
        </div>

        {webSupported && (
          <div className="border-t border-border pt-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-2">
                {webPerm === "granted" ? (
                  <Bell className="w-4 h-4 text-primary mt-0.5" />
                ) : (
                  <BellOff className="w-4 h-4 text-muted-foreground mt-0.5" />
                )}
                <div>
                  <Label className="font-medium">Browser notifications</Label>
                  <p className="text-sm text-muted-foreground">
                    Get a notification when a DM arrives while you're on another tab.
                    Uses your OS notification style and sound.
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Status:{" "}
                    {webPerm === "granted" && <span className="text-primary font-medium">Enabled</span>}
                    {webPerm === "denied" && <span className="text-destructive font-medium">Blocked (update your browser site settings to enable)</span>}
                    {webPerm === "default" && <span>Not enabled yet</span>}
                    {webPerm === "unsupported" && <span>Not supported in this browser</span>}
                  </p>
                </div>
              </div>
              {webPerm === "default" && (
                <Button type="button" size="sm" onClick={handleEnableBrowser}>
                  Enable
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
